import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import PageLoader from '../ui/PageLoader';
import Footer from '../Footer';
import Button from '../ui/Button';
import RegistrationStatusBadge from '../ui/RegistrationStatusBadge';
import PageBackLink from '../ui/PageBackLink';

const POLL_MS = 2500;
const MAX_POLLS = 24;

const PaymentReturnPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const registrationId = searchParams.get('registration_id');
  const sessionId = searchParams.get('session_id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('Confirming payment');
  const [status, setStatus] = useState<string>('payment_pending');
  const [confirmed, setConfirmed] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  const poll = useCallback(async () => {
    if (!registrationId && !sessionId) {
      setError('Missing payment reference');
      setLoading(false);
      return;
    }
    try {
      const data = await apiService.getRegistrationPaymentStatus({
        registration_id: registrationId ? Number(registrationId) : undefined,
        session_id: sessionId || undefined,
      });
      setLabel(data.label);
      setStatus(data.registration.status);
      setConfirmed(data.confirmed);
      if (data.confirmed) {
        setLoading(false);
        return;
      }
      if (!data.confirming && data.registration.status === 'payment_failed') {
        setLoading(false);
        setError('Payment did not complete. You can try again from the event page.');
        return;
      }
      setPollCount((c) => c + 1);
    } catch (err: any) {
      setError(err.message || 'Could not confirm payment');
      setLoading(false);
    }
  }, [registrationId, sessionId]);

  useEffect(() => {
    void poll();
  }, [poll]);

  useEffect(() => {
    if (confirmed || error || pollCount >= MAX_POLLS) {
      if (pollCount >= MAX_POLLS && !confirmed && !error) {
        setLoading(false);
        setError('Payment is still processing. Check back in a few minutes from the event page.');
      }
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void poll();
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [poll, pollCount, confirmed, error]);

  if (loading && !error && !confirmed) {
    return (
      <>
        <PageLoader message="Confirming payment…" />
        <Footer />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-lg mx-auto w-full px-4 py-12 text-center">
        <PageBackLink label="Back to Event" to={`/events/${slug}`} className="mb-6 justify-center" />

        {confirmed ? (
          <>
            <p className="kicker mb-3">Payment confirmed</p>
            <h1 className="heading-section mb-3">Spot confirmed</h1>
            <p className="text-muted text-sm mb-6 leading-relaxed">
              Your entry is confirmed. Check the event page for organizer updates and day-of logistics.
            </p>
            <RegistrationStatusBadge status={status as any} label={label} className="mb-6" />
            <Button variant="primary" onClick={() => navigate(`/events/${slug}`)} className="min-h-[44px]">
              View event
            </Button>
          </>
        ) : (
          <>
            <p className="kicker mb-3">Payment</p>
            <h1 className="heading-section mb-3">{label}</h1>
            <p className="text-muted text-sm mb-6 leading-relaxed">
              {error ||
                'We are waiting for confirmation. Do not close this page yet if you just finished paying.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {!error ? (
                <Button variant="secondary" loading={loading} onClick={() => void poll()} className="min-h-[44px]">
                  Check again
                </Button>
              ) : null}
              <Link to={`/events/${slug}`} className="btn-ghost min-h-[44px] inline-flex items-center justify-center px-4">
                Back to event
              </Link>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PaymentReturnPage;
